// Feature 011 — a pure VIEW over the ingest_run telemetry rows (REQ-STATS-001), shown on the Account
// page: when the mailbox was last checked, and today's breakdown of what the scan did with each email.
// No content ever reaches here — ingest_run holds only integer counts + a timestamp (Principle 2).
import { localDateStr } from "./view";

const TZ = "Pacific/Auckland";

/** One telemetry row (numbers only). Mirrors public.ingest_run. */
export interface IngestRunRow {
  ran_at: string; // ISO instant of the run
  listed?: number;
  filtered?: number; // Promotions/Social/Updates skipped pre-fetch (REQ-INGEST-007)
  diverted?: number; // health-diverted (never fetched)
  held?: number; // unvetted holds (allowlist mode; 0 in denylist)
  captured?: number;
  relevant?: number; // → a must-do draft
  not_relevant?: number; // → judged non-actionable
  failed?: number;
  dead_lettered?: number;
}

/** The four buckets the family cares about, plus totals. `filtered` folds in `held` (both are
 *  "not fetched, ignored" from the reader's point of view). */
export interface IngestBreakdown {
  relevant: number;
  notRelevant: number;
  health: number;
  filtered: number;
  total: number; // relevant + notRelevant + health + filtered
}

export interface IngestStats {
  lastCheck: string | null; // ISO of the most recent run (any day), or null if never run
  runsToday: number;
  today: IngestBreakdown;
  allTime: IngestBreakdown;
  driveConnected: boolean; // content-aware Drive ingestion (008b) live for this family (drive rows exist)
}

const n = (v: number | undefined): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

function breakdown(rows: IngestRunRow[]): IngestBreakdown {
  const b = rows.reduce(
    (a, r) => ({
      relevant: a.relevant + n(r.relevant),
      notRelevant: a.notRelevant + n(r.not_relevant),
      health: a.health + n(r.diverted),
      filtered: a.filtered + n(r.filtered) + n(r.held),
    }),
    { relevant: 0, notRelevant: 0, health: 0, filtered: 0 }
  );
  return { ...b, total: b.relevant + b.notRelevant + b.health + b.filtered };
}

/** Roll the telemetry rows up into the Account stats. `nowISO` fixes "today" in the family's tz.
 *  `driveConnected` reflects whether content-aware Drive ingestion (008b) is live for this family. */
export function ingestStats(rows: IngestRunRow[], nowISO: string, tz = TZ, driveConnected = false): IngestStats {
  const valid = (rows ?? []).filter((r) => r && typeof r.ran_at === "string" && !Number.isNaN(Date.parse(r.ran_at)));
  const lastCheck = valid.reduce<string | null>((best, r) => (best === null || Date.parse(r.ran_at) > Date.parse(best) ? r.ran_at : best), null);
  const todayKey = localDateStr(new Date(nowISO), tz);
  const todayRows = valid.filter((r) => localDateStr(new Date(r.ran_at), tz) === todayKey);
  return {
    lastCheck,
    runsToday: todayRows.length,
    today: breakdown(todayRows),
    allTime: breakdown(valid),
    driveConnected,
  };
}

/** The STANDING mailbox picture (REQ-STATS-002) — computed from the live capture/alert/health rows,
 *  not per-run telemetry, so it's meaningful immediately (unlike "today's activity", which is empty
 *  until a scan runs). `statusCounts` is a tally of ingest captures by status. */
export interface MailboxState {
  toReview: number; // drafts awaiting review (pending + clarifying)
  headsUp: number; // active (undismissed) heads-ups
  filed: number; // committed to the planner
  ignored: number; // discarded as noise / non-actionable
  diverted: number; // kept private (health_queue) — parent-only, else 0
}
export function mailboxState(statusCounts: Record<string, number>, activeAlerts: number, diverted: number): MailboxState {
  const c = (k: string) => n(statusCounts?.[k]);
  return {
    toReview: c("pending") + c("clarifying"),
    headsUp: n(activeAlerts),
    filed: c("committed"),
    ignored: c("discarded"),
    diverted: n(diverted),
  };
}

/** Tally an array of {status} rows into a status→count map (defensive over nulls). */
export function tallyStatus(rows: Array<{ status?: string | null }> | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows ?? []) {
    const s = typeof r?.status === "string" ? r.status : "";
    if (s) out[s] = (out[s] ?? 0) + 1;
  }
  return out;
}

/** "2 hours ago" / "just now" / "yesterday" — a compact relative time for the "Last checked" line.
 *  Pure over the two instants; whole-unit, always past-tense (the run already happened). */
export function relativeTime(fromISO: string | null, nowISO: string): string {
  if (!fromISO) return "never";
  const ms = Date.parse(nowISO) - Date.parse(fromISO);
  if (!Number.isFinite(ms)) return "never";
  if (ms < 60_000) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}
